import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    input,
    OnInit,
    output,
    signal,
    viewChild
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { RouterLink } from "@angular/router";
import { ButtonComponent, ButtonVariant, InputOutline } from "@sxm-content-creators/tools-ds";
import { ROLE_OPTIONS, RoleOption, UserDetail, UserFormValue, UserRole, UserType } from "../../models/user.model";
import { TenantAccessComponent } from "../tenant-access/tenant-access.component";

export type UserFormMode = "create" | "edit";

@Component({
    selector: "app-user-form",
    templateUrl: "./user-form.component.html",
    styleUrl: "./user-form.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        ReactiveFormsModule,
        RouterLink,
        MatFormFieldModule,
        MatSelectModule,
        InputOutline,
        ButtonComponent,
        TenantAccessComponent
    ]
})
export class UserFormComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly destroyRef = inject(DestroyRef);

    readonly mode = input<UserFormMode>("create");
    readonly initialUser = input<UserDetail | null>(null);
    readonly isSubmitting = input(false);
    readonly canDelete = input(false);
    readonly lockRole = input(false);

    readonly formSubmit = output<UserFormValue>();
    readonly deleteRequested = output<void>();

    readonly form = this.formBuilder.group({
        userType: this.formBuilder.control<UserType | null>(null, Validators.required),
        email: this.formBuilder.nonNullable.control("", [Validators.required, Validators.email]),
        role: this.formBuilder.control<UserRole | null>(null, Validators.required),
        firstName: this.formBuilder.nonNullable.control(""),
        lastName: this.formBuilder.nonNullable.control(""),
        tenantIds: this.formBuilder.nonNullable.control<string[]>([])
    });

    private readonly userType = signal<UserType | null>(null);
    private readonly role = signal<UserRole | null>(null);

    private readonly formEvents = toSignal(this.form.events, { initialValue: null });

    readonly isEdit = computed(() => this.mode() === "edit");

    readonly showSiriusXmFields = computed(() => this.userType() === "OKTA");
    readonly showExternalFields = computed(() => this.userType() === "EXTERNAL");
    readonly showRoleField = computed(() => this.userType() !== null);
    readonly showTenantAccess = computed(() => this.requiresTenantAccess(this.role()));

    readonly roleOptions = computed(() => this.roleOptionsFor(this.userType()));

    readonly title = computed(() => (this.isEdit() ? "Edit User" : "Add New User"));
    readonly primaryLabel = computed(() => (this.isEdit() ? "Save" : "Add User"));

    readonly isSubmitDisabled = computed(() => {
        this.formEvents();
        return this.form.invalid || this.isSubmitting();
    });

    readonly emailError = computed(() => {
        this.formEvents();
        const email = this.form.controls.email;
        return email.touched && email.invalid;
    });

    private readonly tenantAccess = viewChild(TenantAccessComponent);
    private readonly tenantAccessEl = viewChild<TenantAccessComponent, ElementRef<HTMLElement>>(TenantAccessComponent, {
        read: ElementRef
    });

    // Flipped once the admin picks a role themselves, which is what separates a reveal worth
    // scrolling to from the first render of a user who is already tenant-scoped.
    private roleChangedByAdmin = false;

    constructor() {
        // A tenant-scoped role adds a section below the fold, so scroll it into view.
        effect(() => {
            const tenantAccess = this.tenantAccessEl()?.nativeElement;
            if (!tenantAccess || !this.roleChangedByAdmin || this.tenantAccess()?.isLoading() !== false) return;

            setTimeout(() => tenantAccess.scrollIntoView({ behavior: "smooth", block: "nearest" }));
        });

        effect(
            () => {
                const role = this.form.controls.role;
                if (this.lockRole()) {
                    role.disable({ emitEvent: false });
                } else {
                    role.enable({ emitEvent: false });
                }
            },
            { allowSignalWrites: true }
        );

        this.form.controls.userType.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(userType => {
            this.userType.set(userType);
            this.applyUserType(userType);
        });

        this.form.controls.role.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
            this.roleChangedByAdmin = true;
            this.role.set(role);

            // Roles that aren't tenant-scoped hide the tenant list.
            if (!this.requiresTenantAccess(role)) {
                this.form.controls.tenantIds.setValue([]);
            }
        });

        this.form.controls.email.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(email => {
            const trimmed = email.trim();
            if (trimmed !== email) {
                this.form.controls.email.setValue(trimmed, { emitEvent: false });
            }
        });
    }

    ngOnInit(): void {
        const user = this.initialUser();
        if (user) {
            this.applyUser(user);
        }

        if (this.isEdit()) {
            this.form.controls.userType.disable();
            this.form.controls.email.disable();
        }
    }

    private applyUser(user: UserDetail): void {
        this.form.patchValue(
            {
                userType: user.userType,
                email: user.email,
                role: user.role,
                firstName: user.firstName ?? "",
                lastName: user.lastName ?? "",
                tenantIds: this.requiresTenantAccess(user.role) ? user.tenants.map(tenant => tenant.id) : []
            },
            { emitEvent: false }
        );

        this.syncNameValidators(user.userType);

        this.userType.set(user.userType);
        this.role.set(user.role);
    }

    // Switching user type mid-form: clear the name fields when the new type gets them from Okta
    // instead of this form, and clear the role if the new type doesn't offer it.
    private applyUserType(userType: UserType | null): void {
        const { firstName, lastName, role } = this.form.controls;

        if (userType !== "EXTERNAL") {
            firstName.reset("", { emitEvent: false });
            lastName.reset("", { emitEvent: false });
        }
        this.syncNameValidators(userType);

        if (role.value && !this.roleOptionsFor(userType).some(option => option.value === role.value)) {
            role.setValue(null);
        }
    }

    // Only external users type their name into this form, so only they have to keep it filled in.
    private syncNameValidators(userType: UserType | null): void {
        const nameValidators = userType === "EXTERNAL" ? [Validators.required, Validators.pattern(/\S/)] : [];
        for (const name of [this.form.controls.firstName, this.form.controls.lastName]) {
            name.setValidators(nameValidators);
            name.updateValueAndValidity();
        }
    }

    private requiresTenantAccess(role: UserRole | null): boolean {
        return role === "PRODUCER" || role === "HOST";
    }

    private roleOptionsFor(userType: UserType | null): readonly RoleOption[] {
        return userType === "EXTERNAL"
            ? ROLE_OPTIONS.filter(option => this.requiresTenantAccess(option.value))
            : ROLE_OPTIONS;
    }

    onSubmit(): void {
        if (this.isSubmitDisabled()) return;

        // Raw, so the identity fields still come through in edit mode, where they're disabled.
        const { userType, email, role, tenantIds, firstName, lastName } = this.form.getRawValue();

        if (!userType || !role) return;

        const value: UserFormValue = { userType, email, role, tenantIds };

        // Okta is the source of truth for a SiriusXM user's name, so only send what we can change.
        if (userType === "EXTERNAL") {
            value.firstName = firstName.trim();
            value.lastName = lastName.trim();
        }

        this.formSubmit.emit(value);
    }

    protected readonly buttonVariant = ButtonVariant;

    protected readonly userTypeOptions: readonly { value: UserType; label: string }[] = [
        { value: "OKTA", label: "SiriusXM user" },
        { value: "EXTERNAL", label: "External user" }
    ];
}
